import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormFieldBaseComponent } from '../form-field-base/form-field-base.component';
import { FormFieldButtons } from '../form-field-buttons/form-field-buttons.component';
import { FormButton } from '../form-field-button/form-field-button.component';
import { Style } from '@app/core/services/core-component.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'tt-textarea',
    templateUrl: './textarea.component.html',
    styleUrls: ['./textarea.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextareaComponent),
            multi: true,
        },
    ],
})
export class TextareaComponent extends FormFieldBaseComponent implements FormFieldButtons, OnInit, ControlValueAccessor {
    /**
     * **Reactive** - The value to display in the text area.
     */
    @Input()
    public ttModel: string = '';

    /**
     * Event emitted when the value in the text area changes.
     */
    @Output()
    public ttModelChange = new EventEmitter<string>();

    private onControlChange: (value: string) => void = () => {};

    private onControlTouched: () => void = () => {};

    /**
     * Event emitted when the textarea element in the component loses focus.
     */
    @Output()
    public ttOnBlur = new EventEmitter();

    @Input()
    public ttButtonParms?: { [key: string]: any };

    @Input()
    public ttButtons?: FormButton[] = [];

    /**
     * Reference to the input element.
     */
    @ViewChild('inputRef')
    inputRef?: ElementRef<HTMLTextAreaElement>;

    /**
     * Ids of elements in the component.
     */
    public id = {
        textarea: crypto.randomUUID(),
    };

    override style: Style = {
        textarea: {},
    };

    /**
     * Handles changes of the textarea value.
     *
     * @param event the new value.
     */
    onModelChange(event: string) {
        this.ttModel = event;
        this.rememberSubject.next(this.base64UrlEncodeUnicode(event));
        this.ttModelChange.emit(event);
        this.onControlChange(event);
    }

    public onBlur() {
        this.onControlTouched();
        this.ttOnBlur.emit();
    }

    public writeValue(value: string | null | undefined): void {
        this.ttModel = value ?? '';
    }

    public registerOnChange(fn: (value: string) => void): void {
        this.onControlChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onControlTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.ttDisabled = isDisabled;
    }

    /**
     * Selects all content in the textarea.
     */
    selectText() {
        if (this.inputRef) {
            this.inputRef.nativeElement.select();
        }
    }

    private async getLastModelState() {
        if (!this.ttOnlyRemember && this.ttRememberId) {
            this.ttModel = this.base64UrlDecode((await this.remember.getLastStatus(this.ttRememberId))[0].variablevalue);
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (!this.ttOnlyRemember && this.ttRememberId) {
            this.getLastModelState();
        }
    }
}
